import { Timestamp } from 'firebase/firestore'

/** File attached to a shipment leg (e.g. gate pass). */
export interface LegFile {
  fileName: string
  storagePath: string
  url: string
  fileSize: number
  fileType: string
  uploadedAt: Timestamp
  uploadedBy: string
}

export type ShipmentLegStatus =
  | 'Draft'
  | 'Pending driver'
  | 'Pending completion'
  | 'Assigned'
  | 'In progress'
  | 'Completed'

export interface ShipmentLegStatusHistoryEntry {
  status: ShipmentLegStatus
  updated_at: Timestamp
  notes?: string
}

export interface ShipmentLegCompanyHistoryEntry {
  company_id: string
  timestamp: Timestamp
}

export type TransportTruckStatus =
  | 'draft'
  | 'assigned'
  | 'awaiting_transport'
  | 'in_transit'
  | 'repair'
  | 'delivered'

export interface TransportTruckStatusHistoryEntry {
  status: TransportTruckStatus
  entered_at: Timestamp
}

/** Per-need status: needed = admin marked required, accepted = transporter accepted, installed = complete */
export type InstallationNeedStatus = 'needed' | 'accepted' | 'installed'

export type InstallationNeedKey = 'anti_theft' | 'cameras' | 'backup_sensors'

export const INSTALLATION_NEED_KEYS: InstallationNeedKey[] = [
  'anti_theft',
  'cameras',
  'backup_sensors',
]

/** Cameras and backup sensors require working-condition proof photos (even after force-close / late complete). */
export const INSTALLATION_NEEDS_REQUIRING_PHOTOS: InstallationNeedKey[] = [
  'cameras',
  'backup_sensors',
]

export interface InstallationStatus {
  anti_theft?: InstallationNeedStatus
  cameras?: InstallationNeedStatus
  backup_sensors?: InstallationNeedStatus
}

/** Per-need: transporter group ID (company) assigned to perform this installation. If unset, any company with a leg can see it. */
export interface InstallationNeedsAssignedCompany {
  anti_theft?: string
  cameras?: string
  backup_sensors?: string
}

/** Per-need: when the need was assigned (or last reassigned) to a company. */
export interface InstallationNeedsAssignedAt {
  anti_theft?: Timestamp
  cameras?: Timestamp
  backup_sensors?: Timestamp
}

/** Working-condition / proof photo attached to an installation need. */
export interface InstallationNeedPhoto {
  storage_path: string
  url?: string
  uploaded_at: Timestamp
  uploaded_by: string
  file_name?: string
}

export interface InstallationNeedsPhotos {
  anti_theft?: InstallationNeedPhoto[]
  cameras?: InstallationNeedPhoto[]
  backup_sensors?: InstallationNeedPhoto[]
}

export interface TransportTruck {
  id: string
  /** Firebase document ID from master_inventory */
  vehicle_id: string
  vin: string
  /** Placeholder - location reference (e.g. pickup, dropoff, repair shop) */
  currentLocationId?: string
  status_history?: TransportTruckStatusHistoryEntry[]
  /** Per-need status: needed | accepted | installed */
  installation_status?: InstallationStatus
  /** Per-need: company (transporter group) assigned to perform this installation */
  installation_needs_assigned_company?: InstallationNeedsAssignedCompany
  /** Per-need: when assigned (or last reassigned) */
  installation_needs_assigned_at?: InstallationNeedsAssignedAt
  /** Soft-hide from active lists; keep the record for audit */
  installation_needs_hidden?: boolean
  installation_needs_hidden_at?: Timestamp | null
  installation_needs_hidden_by?: string | null
  /** Admin force-closed / mark-completed without the normal transporter accept path */
  installation_needs_force_completed?: boolean
  installation_needs_force_completed_at?: Timestamp | null
  installation_needs_force_completed_by?: string | null
  /** Working-condition photos per need (cameras / sensors required until uploaded) */
  installation_needs_photos?: InstallationNeedsPhotos
  created_at: Timestamp
  created_by: string
  updated_at: Timestamp
}

export function hasInstallationNeedPhoto(
  photos: InstallationNeedsPhotos | undefined,
  need: InstallationNeedKey
): boolean {
  return (photos?.[need]?.length ?? 0) > 0
}

/** True when this need still needs a working-condition photo. */
export function installationNeedMissingRequiredPhoto(
  need: InstallationNeedKey,
  status: InstallationStatus | undefined,
  photos: InstallationNeedsPhotos | undefined
): boolean {
  if (!INSTALLATION_NEEDS_REQUIRING_PHOTOS.includes(need)) return false
  if (!status?.[need]) return false
  return !hasInstallationNeedPhoto(photos, need)
}

export function listInstallationNeedsMissingPhotos(
  status: InstallationStatus | undefined,
  photos: InstallationNeedsPhotos | undefined
): InstallationNeedKey[] {
  return INSTALLATION_NEEDS_REQUIRING_PHOTOS.filter((need) =>
    installationNeedMissingRequiredPhoto(need, status, photos)
  )
}

export function isInstallationNeedsCompleted(status: InstallationStatus | undefined): boolean {
  if (!status) return false
  const values = INSTALLATION_NEED_KEYS.map((k) => status[k]).filter(Boolean)
  return values.length > 0 && values.every((v) => v === 'installed')
}

/** Derive current status from the most recent status_history entry. */
export function getTruckStatus(truck: {
  status_history?: TransportTruckStatusHistoryEntry[]
}): TransportTruckStatus {
  const history = truck.status_history
  if (history?.length) return history[history.length - 1].status
  return 'draft'
}

/**
 * Milliseconds (epoch) of the last time this transport truck entered `delivered`
 * (last matching `entered_at` in chronological `status_history`).
 */
export function getLastDeliveredEnteredAtMillis(truck: {
  status_history?: TransportTruckStatusHistoryEntry[]
}): number | null {
  const history = truck.status_history
  if (!history?.length) return null
  let last: number | null = null
  for (const entry of history) {
    if (entry.status !== 'delivered') continue
    const ms = entry.entered_at?.toMillis?.()
    if (ms != null && Number.isFinite(ms)) {
      last = ms
    }
  }
  return last
}

/**
 * For a vehicle that may have multiple `transport_trucks` rows over time, use the row
 * with the greatest `created_at`, and return that truck's last `delivered` transition time.
 */
export function getDeliveredAtMillisForVehicle(
  vehicleId: string,
  trucks: Pick<TransportTruck, 'vehicle_id' | 'status_history' | 'created_at'>[]
): number | null {
  let best: { createdMs: number; deliveredMs: number } | null = null
  for (const truck of trucks) {
    if (truck.vehicle_id !== vehicleId) continue
    const deliveredMs = getLastDeliveredEnteredAtMillis(truck)
    if (deliveredMs == null) continue
    const createdMs = truck.created_at?.toMillis?.() ?? 0
    if (
      !best ||
      createdMs > best.createdMs ||
      (createdMs === best.createdMs && deliveredMs > best.deliveredMs)
    ) {
      best = { createdMs, deliveredMs }
    }
  }
  return best?.deliveredMs ?? null
}

/** Optional media attached to a timeline entry (stored path in Firebase Storage). */
export interface RepairTruckWorkNoteMedia {
  type: 'photo' | 'video' | 'invoice' | 'estimate' | 'damage_photo'
  storage_path: string
}

export type RepairEstimateReviewStatus = 'approved' | 'rejected'

/** Admin decision on a shop-uploaded estimate PDF (per work note). */
export interface RepairEstimateReview {
  status: RepairEstimateReviewStatus
  decided_at: Timestamp
  decided_by: string
  note?: string
}

/** Classification of the work performed during a repair visit. */
export type RepairWorkType = 'body' | 'mechanical'

/** One entry in the work_notes array on a repair_trucks document. */
export interface RepairTruckWorkNote {
  note: string
  timestamp: Timestamp
  user_id: string
  /** Display name when written by API routes that set it */
  user_name?: string
  /** Admin-only notes can be explicitly exposed to repair users when true */
  visible_to_repair_users?: boolean
  media?: RepairTruckWorkNoteMedia
  /** Present when an admin approved or rejected this estimate PDF note */
  estimate_review?: RepairEstimateReview
}

export type RepairTruckWorkStatus = 'Awaiting estimate' | 'in progress' | 'complete'

export interface RepairTruckWorkStatusHistoryEntry {
  status: RepairTruckWorkStatus
  timestamp: Timestamp
  user_id: string
}

export type RepairTruckChecklistAnswer = 'yes' | 'no' | 'na'

export type RepairTruckChecklistMediaType = 'photo' | 'video'

export interface RepairTruckChecklistPhoto {
  storage_path: string
  uploaded_at?: Timestamp
  uploaded_by?: string
  /** Defaults to photo when omitted (legacy checklist attachments). */
  media_type?: RepairTruckChecklistMediaType
  content_type?: string
}

export interface RepairTruckChecklistResponse {
  answer?: RepairTruckChecklistAnswer
  notes?: string
  answered_at?: Timestamp
  answered_by?: string
  /** Optional photos attached to this checklist question answer */
  photos?: RepairTruckChecklistPhoto[]
}

/** Required walkaround video for a checklist visit (intake or complete). Not a question. */
export interface RepairTruckChecklistWalkaroundVideo {
  storage_path: string
  uploaded_at?: Timestamp
  uploaded_by?: string
  content_type?: string
}

export interface RepairTruckInspectionChecklist {
  template_id: string
  template_updated_at?: Timestamp | null
  responses?: Record<string, RepairTruckChecklistResponse>
  /** Required walkaround video; checklist is incomplete without it. */
  walkaround_video?: RepairTruckChecklistWalkaroundVideo | null
  completed_at?: Timestamp | null
}

/** Document in repair_trucks collection: one record per truck entering repair (at a given shop). */
export interface RepairTruck {
  id: string
  transport_truck_id: string
  repair_shop_location_id: string
  /** Queue position inside a repair shop. Lower numbers are higher priority. */
  priority_rank?: number | null
  /**
   * Whether this visit is body or mechanical work. Set explicitly for dual-capability
   * shops, or inferred from the shop's single capability. Drives the damage-photo gate.
   */
  work_type?: RepairWorkType | null
  work_notes: RepairTruckWorkNote[]
  created_at: Timestamp
  work_status_history: RepairTruckWorkStatusHistoryEntry[]
  flagged: boolean
  paid: boolean
  paid_date?: Timestamp | null
  being_painted?: boolean
  being_painted_date?: Timestamp | null
  /**
   * When true, this visit must finish intake before estimate/complete checklist.
   * Absent/false on legacy visits already at a shop when intake launched.
   */
  requires_intake?: boolean
  /** Intake checklist answers for this visit (problems identified on arrival). */
  intake_checklist?: RepairTruckInspectionChecklist
  /** Complete checklist answers (existing inspection checklist). */
  inspection_checklist?: RepairTruckInspectionChecklist
}

/** Derive current work status from most recent history entry. */
export function getRepairTruckWorkStatus(truck: {
  work_status_history?: RepairTruckWorkStatusHistoryEntry[]
}): RepairTruckWorkStatus {
  const history = truck.work_status_history
  if (history?.length) return history[history.length - 1].status
  return 'Awaiting estimate'
}

export type ShipmentPlanLocationType = 'pickup' | 'repair'

/** Distinguishes original delivery plans from return-to-shop after customer delivery. */
export type ShipmentPlanKind = 'standard' | 'post_delivery_repair'

export function getShipmentPlanKind(
  plan: { plan_kind?: ShipmentPlanKind | null } | null | undefined,
): ShipmentPlanKind {
  return plan?.plan_kind === 'post_delivery_repair' ? 'post_delivery_repair' : 'standard'
}

export interface ShipmentPlanLocation {
  /** Firebase document ID from pickup_locations or repair_shop_locations collection */
  location_id: string
  /** Which collection the location_id refers to */
  location_type: ShipmentPlanLocationType
  notes?: string
}

export interface ShipmentLeg {
  from_id: string
  from_type: ShipmentPlanLocationType
  to_id: string
  to_type: ShipmentPlanLocationType
  company_id?: string
  company_history?: ShipmentLegCompanyHistoryEntry[]
  driver_id?: string
  assignment_notes?: string
  /** Optional photo attached with assignment notes when assigning to a driver */
  assignment_notes_photo?: LegFile
  /** Internal dispatch notes editable by transport company throughout the shipment process */
  dispatch_info?: string
  estimated_shipping_price?: number
  /** ISO date string (YYYY-MM-DD) when the assigned driver plans to pick up the truck */
  scheduled_pickup_date?: string
  /** ISO date string (YYYY-MM-DD) when this leg is ready for pickup */
  ready_date?: string
  /** True if leg is ready, false if not ready */
  ready?: boolean
  status_history?: ShipmentLegStatusHistoryEntry[]
  /** Gate passes and other attachments for this leg */
  gate_passes?: LegFile[]
  /** Transport company explicitly rejected this shipment leg assignment */
  rejected?: boolean
  /** Owner/admin/elevated driver asked OTW admins to mark this leg complete (triggers notification on transition to true) */
  admin_completion_requested?: boolean
  /** Admin cancelled the shipping charge for this completed leg (excluded from audit totals) */
  charge_cancelled?: boolean
  /** Required note explaining why the charge was cancelled */
  charge_cancelled_note?: string
  /** When the charge was cancelled */
  charge_cancelled_at?: Timestamp | null
  /** User id who cancelled the charge */
  charge_cancelled_by?: string
}

/** Derive current status from the most recent status_history entry. */
export function getLegStatus(leg: ShipmentLeg): ShipmentLegStatus {
  const history = leg.status_history
  if (history?.length) return history[history.length - 1].status
  return 'Draft'
}

/** Return a new leg with the given status appended to status_history. */
export function appendLegStatus(
  leg: ShipmentLeg,
  newStatus: ShipmentLegStatus,
  updatedAt: Timestamp,
  notes?: string
): ShipmentLeg {
  return {
    ...leg,
    status_history: [
      ...(leg.status_history ?? []),
      { status: newStatus, updated_at: updatedAt, notes },
    ],
  }
}

/** Vehicle condition report at plan level (one truck per plan). */
export interface ShipmentPlanVehicleDamage {
  description: string
  has_damage: boolean
  has_working_cameras: boolean
  has_shelves: boolean
  /** Driver notes for the repair shop when post-delivery destination is a repair shop. */
  repair_shop_notes?: string
  reported_at: Timestamp
  reported_by: string
}

export interface ShipmentPlan {
  id: string
  transport_truck_id: string
  client_user_id?: string
  description?: string
  /** Defaults to `standard` for plans created before this field existed. */
  plan_kind?: ShipmentPlanKind
  locations?: ShipmentPlanLocation[]
  shipment_legs?: ShipmentLeg[]
  /** Vehicle condition report, updated each time photos are uploaded */
  vehicle_damage?: ShipmentPlanVehicleDamage
  created_at: Timestamp
  created_by: string
  updated_at: Timestamp
}

/**
 * True if the plan has any repair stop that was never visited, or whose latest
 * repair_trucks visit for that shop is not work-status `complete`.
 * No plan / no repair stops → false (nothing incomplete).
 */
export function hasIncompleteRepairShopsOnPlan(
  plan: ShipmentPlan | null | undefined,
  repairTrucksForTransportTruck: RepairTruck[],
): boolean {
  const repairStops = (plan?.locations ?? []).filter((loc) => loc.location_type === 'repair')
  if (repairStops.length === 0) return false

  for (const stop of repairStops) {
    const visits = repairTrucksForTransportTruck.filter(
      (rt) => rt.repair_shop_location_id === stop.location_id,
    )
    if (visits.length === 0) return true

    let latest = visits[0]
    let latestMs = latest.created_at?.toMillis?.() ?? 0
    for (let i = 1; i < visits.length; i++) {
      const ms = visits[i].created_at?.toMillis?.() ?? 0
      if (ms >= latestMs) {
        latest = visits[i]
        latestMs = ms
      }
    }
    if (getRepairTruckWorkStatus(latest) !== 'complete') return true
  }

  return false
}

/** Context when inserting a repair shop into an existing leg (e.g. after photo rejection). The first new leg inherits from the split leg; subsequent legs are fresh. */
export interface LegSplitContext {
  /** Index of the existing leg being split (0-based) */
  splitLegIndex: number
}

/** When a middle stop is removed, two legs become one. Data is inherited from the first of the two old legs (extend dropoff to the next stop). */
export interface LegMergeContext {
  /**
   * Index in `existingLegs` of the leg to preserve (`from_*` matches the new leg’s `from`;
   * `to_*` is updated to the new destination). Equals removed location index (old `locations`) minus 1.
   */
  mergeSourceLegIndex: number
}

/** Derive shipment legs from locations. Each consecutive pair becomes a leg. Preserves company_id, driver_id, assignment_notes, dispatch_info, estimated_shipping_price, status, status_history from existing legs when from/to match. When splitContext is provided, the first leg of the split (at splitLegIndex) inherits assignment data from the old leg but drops estimated_shipping_price so the new miles get a new price; the subsequent leg is fresh (Draft, no company/driver/ready/price). When mergeContext is provided, the leg at mergeSourceLegIndex is extended to the new dropoff (do not pass both split and merge). */
export function deriveShipmentLegsFromLocations(
  locations: ShipmentPlanLocation[],
  existingLegs?: ShipmentLeg[],
  splitContext?: LegSplitContext,
  mergeContext?: LegMergeContext
): ShipmentLeg[] {
  const legs: ShipmentLeg[] = []
  const now = Timestamp.now()
  const splitLegIndex = splitContext?.splitLegIndex
  const mergeS = mergeContext?.mergeSourceLegIndex
  const matchesLegPath = (
    candidate: ShipmentLeg | undefined,
    from: ShipmentPlanLocation,
    to: ShipmentPlanLocation
  ) =>
    candidate &&
    candidate.from_id === from.location_id &&
    candidate.to_id === to.location_id &&
    candidate.from_type === from.location_type &&
    candidate.to_type === to.location_type

  for (let i = 0; i < locations.length - 1; i++) {
    const from = locations[i]
    const to = locations[i + 1]

    /**
     * When splitting an existing leg, path matching alone can pick the wrong old leg
     * when a location appears multiple times (e.g. same repair shop reinserted).
     * Prefer index-based inheritance around the split, then fallback to path lookup.
     */
    let existing: ShipmentLeg | undefined
    if (mergeS != null && existingLegs?.length) {
      if (i < mergeS) {
        const sameIndexLeg = existingLegs[i]
        existing = matchesLegPath(sameIndexLeg, from, to)
          ? sameIndexLeg
          : existingLegs.find((e) => matchesLegPath(e, from, to))
      } else if (i > mergeS) {
        const shiftedLeg = existingLegs[i + 1]
        existing = matchesLegPath(shiftedLeg, from, to)
          ? shiftedLeg
          : existingLegs.find((e) => matchesLegPath(e, from, to))
      } else {
        // i === mergeS: new merged leg; resolved via mergeSource, not path match
        existing = existingLegs.find((e) => matchesLegPath(e, from, to))
      }
    } else if (splitLegIndex != null && existingLegs?.length) {
      if (i < splitLegIndex) {
        const sameIndexLeg = existingLegs[i]
        existing = matchesLegPath(sameIndexLeg, from, to)
          ? sameIndexLeg
          : existingLegs.find((e) => matchesLegPath(e, from, to))
      } else if (i === splitLegIndex + 1) {
        // The second leg created by a split must start fresh.
        existing = undefined
      } else if (i > splitLegIndex + 1) {
        const shiftedLeg = existingLegs[i - 1]
        existing = matchesLegPath(shiftedLeg, from, to)
          ? shiftedLeg
          : existingLegs.find((e) => matchesLegPath(e, from, to))
      } else {
        existing = existingLegs.find((e) => matchesLegPath(e, from, to))
      }
    } else {
      existing = existingLegs?.find((e) => matchesLegPath(e, from, to))
    }

    const isMergedLeg =
      mergeS != null && i === mergeS && existingLegs?.[mergeS] != null
    const mergeSource = isMergedLeg ? existingLegs![mergeS!] : null

    /** First leg of a split: inherit from old leg. Subsequent leg: fresh. */
    const isFirstLegOfSplit =
      !isMergedLeg &&
      splitLegIndex != null &&
      i === splitLegIndex &&
      existingLegs?.[splitLegIndex]
    const splitSource = isFirstLegOfSplit ? existingLegs![splitLegIndex!] : null

    const defaultStatus: ShipmentLegStatus = 'Draft'
    const leg: ShipmentLeg = isMergedLeg && mergeSource
      ? {
          from_id: from.location_id,
          from_type: from.location_type,
          to_id: to.location_id,
          to_type: to.location_type,
          company_id: mergeSource.company_id,
          company_history: mergeSource.company_history,
          driver_id: mergeSource.driver_id,
          assignment_notes: mergeSource.assignment_notes,
          assignment_notes_photo: mergeSource.assignment_notes_photo,
          dispatch_info: mergeSource.dispatch_info,
          estimated_shipping_price: mergeSource.estimated_shipping_price,
          scheduled_pickup_date: mergeSource.scheduled_pickup_date,
          ready_date: mergeSource.ready_date,
          ready: mergeSource.ready ?? false,
          rejected: mergeSource.rejected ?? false,
          admin_completion_requested: mergeSource.admin_completion_requested ?? false,
          charge_cancelled: mergeSource.charge_cancelled ?? false,
          charge_cancelled_note: mergeSource.charge_cancelled_note,
          charge_cancelled_at: mergeSource.charge_cancelled_at,
          charge_cancelled_by: mergeSource.charge_cancelled_by,
          status_history:
            mergeSource.status_history ?? [
              { status: defaultStatus, updated_at: now, notes: 'Shipment leg created' },
            ],
          gate_passes: mergeSource.gate_passes,
        }
      : isFirstLegOfSplit
      ? {
          from_id: from.location_id,
          from_type: from.location_type,
          to_id: to.location_id,
          to_type: to.location_type,
          company_id: splitSource?.company_id,
          driver_id: splitSource?.driver_id,
          assignment_notes: splitSource?.assignment_notes,
          assignment_notes_photo: splitSource?.assignment_notes_photo,
          dispatch_info: splitSource?.dispatch_info,
          // The old estimate was for the full leg. An inserted stop changes the miles, so it must be entered again.
          estimated_shipping_price: undefined,
          scheduled_pickup_date: splitSource?.scheduled_pickup_date,
          ready_date: splitSource?.ready_date,
          ready: splitSource?.ready ?? false,
          rejected: splitSource?.rejected ?? false,
          admin_completion_requested: splitSource?.admin_completion_requested ?? false,
          charge_cancelled: splitSource?.charge_cancelled ?? false,
          charge_cancelled_note: splitSource?.charge_cancelled_note,
          charge_cancelled_at: splitSource?.charge_cancelled_at,
          charge_cancelled_by: splitSource?.charge_cancelled_by,
          status_history:
            splitSource?.status_history ?? [
              { status: defaultStatus, updated_at: now, notes: 'Shipment leg created' },
            ],
          gate_passes: splitSource?.gate_passes,
        }
      : existing !== undefined
        ? {
            from_id: from.location_id,
            from_type: from.location_type,
            to_id: to.location_id,
            to_type: to.location_type,
            company_id: existing.company_id,
            driver_id: existing.driver_id,
            assignment_notes: existing.assignment_notes,
            assignment_notes_photo: existing.assignment_notes_photo,
            dispatch_info: existing.dispatch_info,
            estimated_shipping_price: existing.estimated_shipping_price,
            scheduled_pickup_date: existing.scheduled_pickup_date,
            ready_date: existing.ready_date,
            ready: existing.ready ?? false,
            rejected: existing.rejected ?? false,
            admin_completion_requested: existing.admin_completion_requested ?? false,
            charge_cancelled: existing.charge_cancelled ?? false,
            charge_cancelled_note: existing.charge_cancelled_note,
            charge_cancelled_at: existing.charge_cancelled_at,
            charge_cancelled_by: existing.charge_cancelled_by,
            status_history:
              existing.status_history ?? [
                { status: defaultStatus, updated_at: now, notes: 'Shipment leg created' },
              ],
            gate_passes: existing.gate_passes,
          }
        : {
            from_id: from.location_id,
            from_type: from.location_type,
            to_id: to.location_id,
            to_type: to.location_type,
            company_id: undefined,
        driver_id: undefined,
        assignment_notes: undefined,
        assignment_notes_photo: undefined,
        dispatch_info: undefined,
        estimated_shipping_price: undefined,
        scheduled_pickup_date: undefined,
        ready_date: undefined,
            ready: false,
            rejected: false,
            admin_completion_requested: false,
            charge_cancelled: false,
            status_history: [
              { status: defaultStatus, updated_at: now, notes: 'Shipment leg created' },
            ],
          }
    legs.push(leg)
  }
  return legs
}
